import React from 'react';
import { Monitor, Tv, Image, LayoutTemplate, Video, Cast, Presentation, Signal, MonitorPlay, BoxIcon } from 'lucide-react';
import { VideoSource, DisplayMode, PartitionState } from '../types/crestron';
interface VideoPageProps {
  currentSource: VideoSource;
  displayMode: DisplayMode;
  partitionState: PartitionState;
  onSourceSelect: (source: VideoSource) => void;
  onDisplayModeSelect: (mode: DisplayMode) => void;
}
export function VideoPage({
  currentSource,
  displayMode,
  partitionState,
  onSourceSelect,
  onDisplayModeSelect
}: VideoPageProps) {
  const sources = [{
    id: VideoSource.GoogleMeet,
    label: 'Meet',
    icon: Video,
    sub: 'Conferencing',
    color: 'blue'
  }, {
    id: VideoSource.WallPlate1,
    label: 'Wall Plate 1',
    icon: BoxIcon,
    sub: 'HDMI + USB-C',
    color: 'green'
  }, {
    id: VideoSource.WallPlate2,
    label: 'Wall Plate 2',
    icon: BoxIcon,
    sub: 'HDMI + USB-C',
    color: 'green'
  }, {
    id: VideoSource.BrightSign,
    label: 'BrightSign',
    icon: Presentation,
    sub: 'Signage',
    color: 'purple'
  }, {
    id: VideoSource.TV,
    label: 'Tata Play',
    icon: Tv,
    sub: 'Live TV',
    color: 'amber'
  }];
  const displayModes = [{
    id: DisplayMode.FollowMain,
    label: 'Follow Main',
    icon: LayoutTemplate,
    sub: 'Mirror main wall'
  }, {
    id: DisplayMode.Signage,
    label: 'Signage',
    icon: Image,
    sub: 'BrightSign'
  }, {
    id: DisplayMode.TV,
    label: 'Live TV',
    icon: Tv,
    sub: 'Tata Play'
  }];
  return <div className="h-full p-4 grid grid-cols-12 gap-4" data-id="element-316">
      {/* Left: Main Source Selection */}
      <div className="col-span-8 flex flex-col" data-id="element-317">
        <div className="mb-4 flex items-center justify-between" data-id="element-318">
          <div data-id="element-319">
            <h2 className="text-xl font-bold text-white flex items-center gap-2 mb-1" data-id="element-320">
              <div className="p-1.5 bg-blue-600/20 rounded-lg" data-id="element-321">
                <MonitorPlay className="text-blue-400" size={22} strokeWidth={2.5} data-id="element-322" />
              </div>
              Main Video Wall
            </h2>
            <p className="text-xs text-gray-400 ml-9" data-id="element-323">
              21:9 LED • Samsung S-Box
            </p>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-neutral-800 rounded-lg border border-neutral-700" data-id="element-324">
            <Signal size={12} className="text-green-400" data-id="element-325" />
            <span className="text-[10px] font-bold text-gray-400" data-id="element-326">
              WyreStorm
            </span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 flex-1" data-id="element-327">
          {sources.map(source => {
          const isActive = currentSource === source.id;
          const SourceIcon = source.icon;
          return <button key={source.id} onClick={() => onSourceSelect(source.id)} className={`
                  relative flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all
                  hover:scale-[1.02] active:scale-[0.98]
                  ${isActive ? 'bg-gradient-to-br from-blue-600 to-blue-700 border-blue-400 shadow-[0_0_25px_rgba(37,99,235,0.4)] z-10' : 'bg-neutral-800/80 border-neutral-700 hover:border-neutral-500 hover:bg-neutral-750'}
                `} data-id="element-328">
                <div className={`p-3 rounded-lg mb-2 transition-all ${isActive ? 'bg-white/20' : 'bg-neutral-900/80'}`} data-id="element-329">
                  <SourceIcon size={28} strokeWidth={2} className={isActive ? 'text-white' : 'text-gray-400'} data-id="element-330" />
                </div>

                <h3 className={`text-sm font-bold mb-0.5 ${isActive ? 'text-white' : 'text-gray-200'}`} data-id="element-331">
                  {source.label}
                </h3>
                <p className={`text-[10px] ${isActive ? 'text-blue-100' : 'text-gray-500'}`} data-id="element-332">
                  {source.sub}
                </p>

                {isActive && <div className="absolute top-2 right-2" data-id="element-333">
                    <div className="flex items-center gap-1 px-1.5 py-0.5 bg-white/20 rounded-full backdrop-blur-sm" data-id="element-334">
                      <div className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" data-id="element-335"></div>
                      <span className="text-[9px] font-bold text-white uppercase" data-id="element-336">
                        Live
                      </span>
                    </div>
                  </div>}
              </button>;
        })}
        </div>

        {/* Video Wall Status */}
        <div className="mt-3 grid grid-cols-3 gap-3" data-id="element-337">
          <div className="bg-neutral-800/50 rounded-lg p-2 border border-neutral-700" data-id="element-338">
            <div className="text-[10px] text-gray-500 mb-0.5" data-id="element-339">Resolution</div>
            <div className="text-xs font-bold text-green-400" data-id="element-340">3840×1644</div>
          </div>
          <div className="bg-neutral-800/50 rounded-lg p-2 border border-neutral-700" data-id="element-341">
            <div className="text-[10px] text-gray-500 mb-0.5" data-id="element-342">Refresh</div>
            <div className="text-xs font-bold text-green-400" data-id="element-343">60 Hz</div>
          </div>
          <div className="bg-neutral-800/50 rounded-lg p-2 border border-neutral-700" data-id="element-344">
            <div className="text-[10px] text-gray-500 mb-0.5" data-id="element-345">Signal</div>
            <div className="text-xs font-bold text-green-400" data-id="element-346">LOCKED</div>
          </div>
        </div>
      </div>

      {/* Right: Repeater Displays */}
      <div className="col-span-4 flex flex-col bg-gradient-to-br from-neutral-800 to-neutral-900 rounded-xl p-4 border border-neutral-700 shadow-xl" data-id="element-347">
        <div className="mb-4" data-id="element-348">
          <div className="flex items-center gap-1.5 mb-1.5" data-id="element-349">
            <div className="p-1.5 bg-amber-600/20 rounded-lg" data-id="element-350">
              <Cast className="text-amber-400" size={16} strokeWidth={2.5} data-id="element-351" />
            </div>
            <div data-id="element-352">
              <h2 className="text-base font-bold text-white leading-none" data-id="element-353">
                Repeater Displays
              </h2>
              <p className="text-[10px] text-gray-500 mt-0.5" data-id="element-354">
                10× 65" via Video-over-IP
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2.5 flex-1" data-id="element-355">
          {displayModes.map(mode => {
          const isActive = displayMode === mode.id;
          const ModeIcon = mode.icon;
          return <button key={mode.id} onClick={() => onDisplayModeSelect(mode.id)} className={`
                  flex items-center gap-3 p-3 rounded-lg border-2 transition-all
                  ${isActive ? 'bg-gradient-to-r from-green-600 to-green-700 border-green-400 shadow-[0_0_15px_rgba(34,197,94,0.3)]' : 'bg-neutral-900/80 border-neutral-700 hover:border-neutral-500 hover:bg-neutral-800'}
                `} data-id="element-356">
                <div className={`p-2 rounded-lg ${isActive ? 'bg-white/20' : 'bg-neutral-800'}`} data-id="element-357">
                  <ModeIcon size={20} strokeWidth={2} className={isActive ? 'text-white' : 'text-gray-400'} data-id="element-358" />
                </div>

                <div className="flex-1 text-left" data-id="element-359">
                  <h3 className={`text-xs font-bold mb-0.5 ${isActive ? 'text-white' : 'text-gray-200'}`} data-id="element-360">
                    {mode.label}
                  </h3>
                  <p className={`text-[10px] ${isActive ? 'text-green-100' : 'text-gray-500'}`} data-id="element-361">
                    {mode.sub}
                  </p>
                </div>

                {isActive && <div className="h-2.5 w-2.5 rounded-full bg-white animate-pulse" data-id="element-362"></div>}
              </button>;
        })}
        </div>

        {/* Partition Status */}
        <div className="mt-4 pt-3 border-t border-neutral-700" data-id="element-363">
          <div className="flex items-center justify-between mb-2" data-id="element-364">
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-365">
              Zone Status
            </span>
            <div className={`px-2 py-0.5 rounded text-[10px] font-bold ${partitionState === PartitionState.Open ? 'bg-green-900/40 text-green-400' : 'bg-amber-900/40 text-amber-400'}`} data-id="element-366">
              {partitionState === PartitionState.Open ? 'UNIFIED' : 'SPLIT'}
            </div>
          </div>

          <div className="space-y-1.5 text-[10px]" data-id="element-367">
            <div className="flex justify-between items-center" data-id="element-368">
              <span className="text-gray-400" data-id="element-369">Zone A:</span>
              <span className="text-white font-bold" data-id="element-370">6 Displays</span>
            </div>
            {partitionState === PartitionState.Closed && <div className="flex justify-between items-center" data-id="element-371">
                <span className="text-gray-400" data-id="element-372">Zone B:</span>
                <span className="text-amber-400 font-bold" data-id="element-373">
                  4 • Auto Signage
                </span>
              </div>}
            <div className="flex justify-between items-center pt-1.5 border-t border-neutral-800" data-id="element-374">
              <span className="text-gray-400" data-id="element-375">Mode:</span>
              <span className="text-blue-400 font-bold" data-id="element-376">{displayMode}</span>
            </div>
          </div>
        </div>
      </div>
    </div>;
}